import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { isEmpty } from 'lodash-es';

import {
  MultichannelNames,
  PaidFeature,
  SubscriptionPlan,
  UserPropertyEnum,
  UserRoleType,
} from '@vi-iot/client';

import {
  AnalyticsClientRole,
  APPLICATION_ROLES,
  AuthState,
  BUSINESS_ROLES,
  ConnectivityRankingPositions,
  DashboardUserType,
  InstallationDetailsState,
  InstallationsListInitModel,
  MeUserDetails,
  RemoteConfigSelectors,
  RemoteConfigState,
  RemoteDiagnosticsAccessStatus,
} from '@store';

import { AppState } from '../app.state';
import { InstallationsSelectors } from '../installations';

import { isRsa } from '../_common/role-helpers';
import { LanguageState } from './../settings/language/language.state';
import { IAMRoles, VkOrgCountry } from './../_enums';
import { mapUserRoleTypeToGroupType } from './auth.helpers';
import { requiredApplicationRoles, xotaApplicationRoles } from './auth.model';
export namespace AuthSelectors {
  export const authState = (state: AppState): AuthState => state.auth;
  export const languageState = (state: AppState): LanguageState => state.language;
  export const installationDetailsState = (state: AppState): InstallationDetailsState =>
    state.installationDetails;
  export const remoteConfig = (state: AppState): RemoteConfigState => state.remoteConfig;

  export function getState(): MemoizedSelector<AppState, AuthState, DefaultProjectorFn<AuthState>> {
    return createSelector(authState, (state: AuthState) => state);
  }

  export function analyticsClientGroup(): MemoizedSelector<
    AppState,
    AnalyticsClientRole,
    DefaultProjectorFn<AnalyticsClientRole>
  > {
    return createSelector(
      AuthSelectors.isRsaUser(),
      (isRsaUserInput: boolean | null): AnalyticsClientRole => {
        if (isRsaUserInput === null) {
          return AnalyticsClientRole.unknown;
        } else {
          return isRsaUserInput ? AnalyticsClientRole.internal : AnalyticsClientRole.external;
        }
      }
    );
  }

  export function selectAuthenticatedState(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState) => state.authenticated);
  }

  export function isAdminOrSupportInternational(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState) => state.authorizedAs.admin || state.authorizedAs.supportInternational
    );
  }

  export function isRsaUser(): MemoizedSelector<
    AppState,
    boolean | null,
    DefaultProjectorFn<boolean | null>
  > {
    return createSelector(authState, (state: AuthState) => {
      if (state.me?.iotRole) {
        return isRsa(mapUserRoleTypeToGroupType(state.me.iotRole));
      } else {
        return null;
      }
    });
  }

  export function selectIsAccessFeedbackApplicationRole(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState): boolean => {
      const isAccessFeedbackApplicationRole = state.me?.applicationRoles.some(
        (role) => role === APPLICATION_ROLES.RSA_ACCESS_FEEDBACK
      );

      return isAccessFeedbackApplicationRole;
    });
  }

  export function isRsaUserWithWriteRights(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState) =>
        state.authorizedAs.admin ||
        state.me?.iotRole === UserRoleType.supportLocalReadWrite ||
        state.me?.iotRole === UserRoleType.supportLocalReadWriteWithRemoteDiagnosis ||
        state.me?.iotRole === UserRoleType.supportInternationalWithRemoteDiagnosis ||
        state.me?.iotRole === UserRoleType.supportInternational
    );
  }

  export function isMaintainerOrUser(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState) =>
        // Currently endUser (User group) is allowed to access to Vitoguide
        state.authorizedAs.user || state.authorizedAs.maintainer
    );
  }

  export const isMaintainer = createSelector(
    authState,
    (state): boolean => state.authorizedAs.maintainer
  );

  export function isEndUser(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(authState, (state: AuthState) => {
      const isEndCustomer = !!state.me?.properties?.find(
        (property) => property.value === IAMRoles.endCustomer
      );

      return isEndCustomer;
    });
  }

  export const selectIsEndUserWithoutAccess = createSelector(
    isEndUser(),
    remoteConfig,
    (isEndUser, remoteConfigState) => isEndUser && remoteConfigState.remoteConfig.blockEndCustomers
  );

  export function isLiteAccount(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState) => {
      const liteAccount = state.me?.businessRoles?.find(
        (role) => role === BUSINESS_ROLES.DEFAULT_LITE_ACCOUNT
      );

      return !!liteAccount;
    });
  }

  export function isRequiredApplicationRole(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState) => {
      const isRequiredApplicationRole = requiredApplicationRoles.some((role) =>
        state.me?.applicationRoles?.includes(role)
      );

      return isRequiredApplicationRole;
    });
  }

  export function isXotaApplicationRole(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState) => {
      const isXotaApplicationRole = xotaApplicationRoles.some((role) =>
        state.me?.applicationRoles?.includes(role)
      );

      return isXotaApplicationRole;
    });
  }

  export function isRsaXotaApplicationRole(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState) => {
      const isRsaXotaApplicationRole = state.me?.applicationRoles.some(
        (role) => role === APPLICATION_ROLES.RSA_XOTA_UPDATE_BUTTON
      );

      return isRsaXotaApplicationRole;
    });
  }

  export const isCommercialUser = createSelector(authState, (state: AuthState) => {
    const hasProperMultichannel =
      state.me?.company?.multichannel === MultichannelNames.Utilities ||
      state.me?.company?.multichannel === MultichannelNames.HousingSocieties;

    return hasProperMultichannel;
  });

  export const selectDashboardFeatureIfAvailable = createSelector(
    authState,
    isCommercialUser,
    (state: AuthState, isCommercial: boolean): boolean => {
      return isCommercial
        ? state.availableCompanyPaidFeatures.some((feature) => feature === PaidFeature.Dashboard)
        : true;
    }
  );

  export const isUserWithRoleInvitation = createSelector(authState, (state: AuthState): boolean => {
    const hasProperApplicationRole = state.me?.applicationRoles?.some(
      (role) => role === APPLICATION_ROLES.NEVIS_VITOGUIDE_INVITATION
    );

    return hasProperApplicationRole;
  });

  export const isUserOfCommercialOwnerCompanyWithRoleInvitation = createSelector(
    authState,
    selectIsCompanyIdIsSameAsCommercialOwner(),
    (state: AuthState, isCompanyIdIsSameAsCommercialOwner: boolean): boolean => {
      const hasProperApplicationRole = state.me?.applicationRoles?.some(
        (role) => role === APPLICATION_ROLES.NEVIS_VITOGUIDE_INVITATION
      );

      return isCompanyIdIsSameAsCommercialOwner && hasProperApplicationRole;
    }
  );

  export const selectIsUserWithHeatingSystemsOverviewRole = createSelector(
    authState,
    (state: AuthState): boolean => {
      const hasHeatingSystemsOverviewRole = state.me?.applicationRoles?.some(
        (role) =>
          role === APPLICATION_ROLES.VIGUIDE_HEATING_SYSTEMS_OVERVIEW ||
          role === APPLICATION_ROLES.VITOGUIDE_HEATING_SYSTEMS_OVERVIEW_V2
      );

      return hasHeatingSystemsOverviewRole;
    }
  );

  export const selectIsUserWithVitoguideDetaultRole = createSelector(
    authState,
    (state: AuthState): boolean => {
      const hasVitoguideDefaultRole = state.me?.applicationRoles?.some(
        (role) => role === APPLICATION_ROLES.VITOGUIDE_DEFAULT
      );

      return hasVitoguideDefaultRole;
    }
  );

  export const selectIsUserNavigateToHeatingSystemsOverviewExternal = createSelector(
    selectIsUserWithHeatingSystemsOverviewRole,
    selectIsUserWithVitoguideDetaultRole,
    (hasHeatingSystemsOverviewRole, hasVitoguideDefaultRole): boolean =>
      hasHeatingSystemsOverviewRole && !hasVitoguideDefaultRole
  );

  export function getUserRoles(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(authState, (state: AuthState) => state.me?.applicationRoles);
  }

  export const hasUserRolesForRemoteDiagnosis = (): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > =>
    createSelector(authState, (state: AuthState) => {
      const hasRemoteDiagnosisRole = hasUserRoles(state, [
        UserRoleType.supportLocalReadWriteWithRemoteDiagnosis,
        UserRoleType.supportInternationalWithRemoteDiagnosis,
        UserRoleType.admin,
      ]);

      return state.authorized && !isEmpty(state.me?.applicationRoles) && hasRemoteDiagnosisRole;
    });

  export function getMe(): MemoizedSelector<
    AppState,
    MeUserDetails,
    DefaultProjectorFn<MeUserDetails>
  > {
    return createSelector(
      authState,
      (state: AuthState): MeUserDetails => ({
        companyCountryCode: state.me?.company?.countryCode || '',
        companyId: state.me?.company?.id || '',
        companyName: state.me?.company?.name || '',
        vkorg: state.me?.company?.vkorg || '',
        email: state.me?.contacts?.email || '',
        loginId: state.me?.loginId || '',
        familyName: state.me?.name?.familyName || '',
        firstName: state.me?.name?.firstName || '',
        iotRole: state.me?.iotRole || null,
        phone: state.me?.contacts?.telephone || state.me?.contacts?.mobile,
        roles: state.me?.applicationRoles || [],
      })
    );
  }

  export function getCompanyId(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState) => {
      const companyId = state.me?.company?.id;

      return companyId;
    });
  }

  export function getName(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState) => {
      const firstName = state.me?.name?.firstName;
      const familyName = state.me?.name?.familyName;

      return `${firstName} ${familyName}`.trim();
    });
  }

  export function getPhone(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      authState,
      (state: AuthState): string => state.me?.contacts?.telephone || state.me?.contacts?.mobile
    );
  }

  export function getVkorg(): MemoizedSelector<
    AppState,
    string | undefined,
    DefaultProjectorFn<string | undefined>
  > {
    return createSelector(
      authState,
      (state: AuthState): string | undefined => state.me?.company?.vkorg
    );
  }

  export function isCompanyFromUsaOrCanada(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState): boolean =>
        state.me?.company?.vkorg?.includes(VkOrgCountry.Canada) ||
        state.me?.company?.vkorg?.includes(VkOrgCountry.USA)
    );
  }

  export function isCompanyFromGermany(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState): boolean =>
      (state.me?.company?.vkorg || '').includes(VkOrgCountry.Germany)
    );
  }

  export function selectIsCompanyFromGermanyOrAustria(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (state: AuthState): boolean =>
      [VkOrgCountry.Germany, VkOrgCountry.Austria].some((vkOrg) =>
        (state.me?.company?.vkorg || '').includes(vkOrg)
      )
    );
  }

  export function isCompanyFromNetherlandsOrPoland(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState): boolean =>
        state.me?.company?.vkorg?.includes(VkOrgCountry.Netherlands) ||
        state.me?.company?.vkorg?.includes(VkOrgCountry.Poland)
    );
  }

  export function getCountryCode(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState): string => state.me?.company?.countryCode);
  }

  export function getAddressCountryCode(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(authState, (state: AuthState): string => state.me?.address?.countryCode);
  }

  export function getLanguageCode(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(authState, (state: AuthState): string => state.me?.languageCode);
  }

  export function getCompanyName(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState): string => state.me?.company?.name);
  }

  export function getUserId(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState): string => state.me?.id);
  }

  export function getEmail(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState): string => state.me?.contacts?.email);
  }

  export function getLoginId(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(authState, (state: AuthState): string => state.me?.loginId);
  }

  export function getUserIotRole(): MemoizedSelector<
    AppState,
    UserRoleType,
    DefaultProjectorFn<UserRoleType>
  > {
    return createSelector(authState, (state: AuthState) => state.me?.iotRole);
  }

  export function getIdentityTypeValue(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(authState, (state: AuthState) => {
      const identityTypeProperty = state.me?.properties?.find(
        (item) => item.name === UserPropertyEnum.IdentityType
      );

      return identityTypeProperty?.value;
    });
  }

  export function hasOneOfUserIotRoles(
    userIotRoles: UserRoleType | UserRoleType[]
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(authState, (state: AuthState) => {
      if (Array.isArray(userIotRoles)) {
        return userIotRoles.includes(state.me?.iotRole);
      }

      return state.me?.iotRole === userIotRoles;
    });
  }

  export function isLearningRoomVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState) =>
        state.authorizedAs?.maintainer &&
        state.me?.company?.countryCode === 'DE' &&
        state.learningRoom
    );
  }

  export function isNewVitoguideFeaturesBannerVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, languageState, (auth: AuthState) => auth.eventBanner);
  }

  export function isPaidFeaturesBannerVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(authState, (auth: AuthState) => auth.paidFeaturesBanner);
  }

  export const isDemo: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(authState, (state: AuthState): boolean => state.demo);

  export function userHasAccessToInvitations(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState) =>
        !!state.me?.applicationRoles?.find(
          (role) => role === APPLICATION_ROLES.NEVIS_VITOGUIDE_INVITATION
        )
    );
  }

  export function userHasAccessToNotificationSettings(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      (state: AuthState) =>
        !!state.me?.applicationRoles?.find(
          (role) => role === APPLICATION_ROLES.VITOGUIDE_CAN_MANAGE_NOTIFICATIONS
        )
    );
  }

  export function userCanManageSubscriptions(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      remoteConfig,
      AuthSelectors.isCompanyFromUsaOrCanada(),
      (state: AuthState, remoteConfigState: RemoteConfigState, isUsaOrCanadaCompany: boolean) => {
        // https://jira.viessmann-climatesolutions.com/browse/VIG-12702
        if (isUsaOrCanadaCompany) {
          return (
            !!state.me?.applicationRoles?.find(
              (role) => role === APPLICATION_ROLES.VITOGUIDE_CAN_MANAGE_SUBSCRIPTIONS
            ) && remoteConfigState?.remoteConfig?.isCanadaAndUSABookingAvailable
          );
        } else {
          return !!state.me?.applicationRoles?.find(
            (role) => role === APPLICATION_ROLES.VITOGUIDE_CAN_MANAGE_SUBSCRIPTIONS
          );
        }
      }
    );
  }

  export const hasAccessToDiagnostics = (): MemoizedSelector<
    AppState,
    RemoteDiagnosticsAccessStatus,
    DefaultProjectorFn<RemoteDiagnosticsAccessStatus>
  > =>
    createSelector(
      authState,
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      (state: AuthState, hasRemoteDiagnosisRole: boolean): RemoteDiagnosticsAccessStatus => ({
        hasAccessToDiagnostics:
          state.authorized && (hasRemoteDiagnosisRole || state.authorizedAs?.maintainer),
        isStoreReady: !!state.me?.iotRole && !isEmpty(state.authorizedAs) && !!state.authorized,
      })
    );

  export function canEditBmuSerial(
    rolesToHave: string[]
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(authState, (state: AuthState) => {
      const hasRolesToEdit = hasUserRoles(state, rolesToHave);

      return state.authorized && hasRolesToEdit;
    });
  }

  export const getCompanyAddressAsString = createSelector(authState, (state): string => {
    const address = state.me?.address;
    if (!address) {
      return null;
    }
    const combinedAddress = `${address.street ?? ''} ${address.houseNumber ?? ''}, ${
      address.postalCode ?? ''
    } ${address.city ?? ''}`;

    return combinedAddress.trim() !== ',' ? combinedAddress : null;
  });

  export const getCompanyIdForInstallationsListInit = createSelector(
    InstallationsSelectors.selectInstallations(),
    AuthSelectors.isRsaUser(),
    getCompanyId(),
    (installations, isRsaUserStatus, companyId): InstallationsListInitModel => {
      return {
        companyId,
        isRsaUser: isRsaUserStatus,
        installationsCount: installations.length,
      };
    }
  );

  function hasUserRoles(state: AuthState, rolesToHave: string[]): boolean {
    return rolesToHave.some((role) => role === state.me?.iotRole);
  }

  export const isRsaOrMaintainer = createSelector(
    isRsaUser(),
    isMaintainer,
    (isRsaValue: boolean | null, isMaintainerValue: boolean): boolean =>
      (isRsaValue || isMaintainerValue) ?? false
  );

  export function getAvailablePaidCompanyFeatures(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(authState, (state: AuthState) => state.availableCompanyPaidFeatures);
  }

  export function selectCompanySubscriptionPlan(): MemoizedSelector<
    AppState,
    SubscriptionPlan,
    DefaultProjectorFn<SubscriptionPlan>
  > {
    return createSelector(authState, (state: AuthState) => state.companySubscriptionPlan);
  }

  export const selectAuthorized = createSelector(authState, (state): boolean => !!state.authorized);

  export const selectIsAuthorizing = createSelector(
    authState,
    (state): boolean => state.authorizing
  );

  export function selectAvailableGlobalPaidFeaturePresence(
    feature: PaidFeature
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(authState, (state): boolean =>
      state.availableCompanyPaidFeatures.includes(feature)
    );
  }

  export const selectConnectivityRankingPositions = createSelector(
    authState,
    (state): ConnectivityRankingPositions | null => {
      if (!state.connectivityRanking) {
        return null;
      }

      return {
        userPosition: state.connectivityRanking.sales_org_ranking,
        allPositions: state.connectivityRanking.total_connected_installations,
      };
    }
  );

  export function selectIsCompanyIdIsSameAsCommercialOwner(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      installationDetailsState,
      (state: AuthState, installationDetails: InstallationDetailsState): boolean => {
        const companyId = state.me?.company?.id;
        const commercialCustomerNumber =
          installationDetails?.accessList?.commercialOwner?.customerNumber;

        return companyId === commercialCustomerNumber;
      }
    );
  }
  export function selectServiceTicketAccess(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      authState,
      RemoteConfigSelectors.selectServiceTicketVkorgList,
      RemoteConfigSelectors.selectServiceTicketAllowedByCompanyId,
      (
        state: AuthState,
        allowedVkorgs: string[],
        serviceTicketAllowedByCompanyId: boolean
      ): boolean => {
        const vkorg = state.me?.company?.vkorg;

        if (!vkorg) {
          return false;
        }

        const isAllowedByVkorg = allowedVkorgs.some((allowedVkorg) => vkorg.includes(allowedVkorg));

        return isAllowedByVkorg || serviceTicketAllowedByCompanyId || state.demo;
      }
    );
  }

  export const selectDashboardUserType = createSelector(
    isRsaUser(),
    isEndUser(),
    isMaintainer,
    (rsa, endUser, maintainer): DashboardUserType | undefined => {
      if (rsa) {
        return DashboardUserType.Rsa;
      } else if (endUser) {
        return DashboardUserType.EndUser;
      } else if (maintainer) {
        return DashboardUserType.Maintainer;
      }
    }
  );

  export const selectIsAuthenticating = createSelector(
    authState,
    (state: AuthState): boolean => state.authenticating
  );

  export const selectPrimaryVkorg = createSelector(
    authState,
    (state: AuthState): string =>
      state.company.value?.salesOrganizationData.find(
        (item) => item.Id === state.company.value.primarySalesOrganizationData
      ).ExtSapVKOrg
  );
}
